import * as React from 'react'
import { Switch, tokens, Tooltip } from '@fluentui/react-components'
import { Color20Filled } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useTimeCalendarStyles } from './timeCalendarStyles'

// Referenced from the stylesheet, so both sides have to agree on the name.
const GRADIENT_ID = 'timeCalendarColorsGradient'

type Props = {
    checked: boolean
    onChange: (checked: boolean) => void
}

/**
 * Switches the colour scheme of the time writing calendar on and off (HAM-4315). Only rendered when the office
 * leaves the choice to the user: with 'colours required' the colours cannot be hidden and there is no switch.
 * The label is an icon on every viewport, so the tooltip is the accessible name. While the colours are on the
 * icon shows the three cell colours as a gradient; off it stays neutral, matching what the table looks like.
 */
export default function CalendarColorsSwitch(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate } = useTranslation()

    const label = (
        <>
            <svg className={styles.colorsGradientDefs} aria-hidden focusable={'false'}>
                <defs>
                    {/* Same order as the legend: red for an empty day, marigold below the threshold, green
                        above it. Foreground1 rather than the Foreground2 of the cells: at icon size the
                        cell colours turn muddy. */}
                    <linearGradient id={GRADIENT_ID} x1={'0'} y1={'1'} x2={'1'} y2={'0'}>
                        <stop offset={'0%'} stopColor={tokens.colorPaletteRedForeground1} />
                        <stop offset={'50%'} stopColor={tokens.colorPaletteMarigoldForeground1} />
                        <stop offset={'100%'} stopColor={tokens.colorPaletteGreenForeground1} />
                    </linearGradient>
                </defs>
            </svg>
            <Color20Filled className={props.checked ? styles.colorsIcon : undefined} />
        </>
    )

    return (
        <Tooltip withArrow relationship={'label'} content={translate('time.calendar.colors.tooltip')}>
            <Switch
                size={'small'}
                className={styles.colorsSwitch}
                checked={props.checked}
                label={label}
                onChange={(_event, data) => props.onChange(data.checked)}
            />
        </Tooltip>
    )
}
